/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    HTTPProtocol,
    IHTTPRewriteLocHdrAction,
    IURIParam,
    IURIParamToken,
    URIParamType,
} from 'generated-types';

import {
    isEmpty,
    reduce,
} from 'underscore';

import { HTTPRewriteLocHdrAction } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { DefaultValues } from 'ajs/modules/core/services/default-values.service';

import {
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories/repeated-message-item.factory';

type THTTPRewriteLocHdrActionPartial = Omit<IHTTPRewriteLocHdrAction, 'host' | 'path'>;

type TURIParamPartial = Omit<IURIParam, 'tokens'>;

/**
 * Interface to update tokens as repeatedMessageItem.
 */
interface IURIParamConfig extends TURIParamPartial {
    tokens: RepeatedMessageItem<MessageItem<IURIParamToken>>,
}

/**
 * Extended IHTTPRewriteLocHdrAction Interface to add UI only fields.
 */
interface IHTTPRewriteLocHdrActionConfig extends THTTPRewriteLocHdrActionPartial {
    host?: MessageItem<IURIParamConfig>,
    path?: MessageItem<IURIParamConfig>,
    hostStr?: string,
    pathStr?: string,
}

/**
 * @description HTTPRewriteLocHdrAction ConfigItem.
 *
 * @author Abhinesh Gour
 */
export class HTTPRewriteLocHdrActionConfigItem extends MessageItem<IHTTPRewriteLocHdrActionConfig> {
    public static ajsDependencies = [
        'RangeParser',
    ];

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPRewriteLocHdrAction,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * @override
     * Convert host and path tokens to string values required on UI.
     */
    public modifyConfigDataAfterLoad(): void {
        const { host, path } = this.config;

        super.modifyConfigDataAfterLoad();

        if (!host) {
            this.safeSetNewChildByField('host');
        } else if (host.config?.tokens?.count) {
            this.config.hostStr = this.extractStringValue(host.config.tokens, '.');
        }

        if (!path) {
            this.safeSetNewChildByField('path');
        } else if (path.config?.tokens?.count) {
            const value = this.extractStringValue(path.config.tokens, '/');

            this.config.pathStr = !isEmpty(value) && value || '/';
        }
    }

    /**
     * @override
     * Convert host and path string values to tokenised params.
     */
    public modifyConfigDataBeforeSave(): void {
        const { hostStr, pathStr } = this.config;

        const rangeParser = this.getAjsDependency_('RangeParser');

        super.modifyConfigDataBeforeSave();

        if (hostStr) {
            this.config.host.updateConfig({
                type: URIParamType.URI_PARAM_TYPE_TOKENIZED,
                tokens: rangeParser.tokensStr2tokens(hostStr, '.'),
            });

            delete this.config.hostStr;
        } else {
            delete this.config.host;
        }

        if (pathStr) {
            this.config.path.updateConfig({
                type: URIParamType.URI_PARAM_TYPE_TOKENIZED,
                tokens: rangeParser.tokensStr2tokens(pathStr, '/'),
            });

            delete this.config.pathStr;
        } else {
            delete this.config.path;
        }
    }

    /**
     * Extracts value from the config and prepares a string
     * to show action details in the grid expander.
     */
    public actionReadonlyDetails(): string {
        const rangeParser = this.getAjsDependency_('RangeParser');

        const { host, path, protocol, keep_query: keepQuery, port } = this.config;
        let hostStr = '<ExistingHost>';
        let pathStr = '<ExistingPath>';

        if (host?.config?.tokens?.count) {
            const { tokens } = host?.config || {};

            hostStr = tokens.config.map(token => rangeParser.token2str(token?.config)).join('.');
        }

        if (path?.config?.tokens?.count) {
            const { tokens } = path?.config || {};

            pathStr = tokens.config.map(token => rangeParser.token2str(token?.config)).join('.');
        }

        const query = keepQuery ? '<KeepQueryString>' : '<RemoveQueryString>';

        return `${protocol.toLowerCase()}://${hostStr}:${port}/${pathStr}?${query}`;
    }

    /**
     * Convert host and path tokens to formatted string value.
     */
    private extractStringValue(
        tokens: RepeatedMessageItem<MessageItem<IURIParamToken>>,
        separator: string,
    ): string {
        const rangeParser = this.getAjsDependency_('RangeParser');

        return reduce(tokens.config, (base, token) => {
            return `${base + rangeParser.token2str(token.config)}${separator}`;
        }, '').slice(0, -1);
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IHTTPRewriteLocHdrActionConfig> {
        const defaultValues: DefaultValues = this.getAjsDependency_('defaultValues');
        const type = this.objectType.toLowerCase();
        const defaultConfig = defaultValues.getDefaultItemConfigByType(type) || {};

        return {
            ...defaultConfig,
            protocol: HTTPProtocol.HTTP,
            port: 80,
        };
    }
}
